import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { markNoticeSeen, NOTICES_KEY, useHandoverNotices, type HandoverNoticeOut } from "@/api";
import { Button } from "@/components/Button";
import { Figures } from "@/components/Figures";
import { CheckIcon, ClockIcon, ReturnIcon } from "@/components/icons";
import { StateMessage } from "@/components/StateMessage";
import { activityTime } from "@/lib/format";
import { useNarrow } from "@/lib/useNarrow";

import styles from "./Incoming.module.css";
import { useArrivals } from "./useArrivals";

/** A sentence ends with a stop — the server's own details do, a typed reason or a fallback title may not. */
export function stop(text: string): string {
  return /[.!?…]$/.test(text) ? text : `${text}.`;
}

/**
 * Somebody else's words, set off as such (UX gate round 2, #4): without the
 * quotation marks a typed reason read like the system's own sentence. The
 * stop stays inside the quote when the person wrote one, and closes the
 * sentence after it when not (Task 13 review, M1: without one it ran into
 * the meta line).
 */
export function quoted(text: string): string {
  const said = text.trim();
  return /[.!?…]$/.test(said) ? `„${said}“` : `„${said}“.`;
}

/** The one sentence the sender reads once (design §8.3, UX ruling 11). */
export function noticeSentence(notice: HandoverNoticeOut): string {
  const { handover, caseTitle } = notice;
  const title = `„${caseTitle}“`;
  switch (handover.state) {
    case "accepted":
      // „Er" had nothing to point back to (round 2, #4) — the Vorgang,
      // named, as the record's own withdrawal says it.
      return `${handover.toDisplayName} hat ${title} übernommen. Der Vorgang steht nicht mehr in deinem Register.`;
    case "declined":
      return handover.declineReason && handover.declineReason.trim() !== ""
        ? `${handover.toDisplayName} hat ${title} abgelehnt: ${quoted(handover.declineReason)}`
        : `${handover.toDisplayName} hat ${title} abgelehnt.`;
    case "expired":
      return `Die Anfrage an ${handover.toDisplayName} für ${title} ist verfallen.`;
    default:
      return `Die Übergabe von ${title} ist beendet.`;
  }
}

function noticeId(notice: HandoverNoticeOut): string {
  return notice.handover.id;
}

/**
 * A declined or lapsed request leaves the Vorgang with me: that notice asks
 * for something — hand it on again, or take it up — and a taken-over one
 * asks for nothing (UX gate round 2, #4).
 */
export function noticeAsks(notice: HandoverNoticeOut): boolean {
  return notice.handover.state === "declined" || notice.handover.state === "expired";
}

/**
 * The notices by need (round 2, #4): what asks for something first, in the
 * server's order within each kind. Only the first stands open (S-7), so it
 * is the one that wants an answer.
 */
export function byNeed(notices: readonly HandoverNoticeOut[]): HandoverNoticeOut[] {
  return [...notices.filter(noticeAsks), ...notices.filter((notice) => !noticeAsks(notice))];
}

/**
 * Its own symbol per outcome, on one text edge (round 2, #4): taken over a
 * check, declined an arrow back, lapsed a clock. The words say it; the
 * symbol lets a list of them be told apart at a glance.
 */
function noticeIcon(notice: HandoverNoticeOut) {
  switch (notice.handover.state) {
    case "accepted":
      return <CheckIcon />;
    case "declined":
      return <ReturnIcon />;
    case "expired":
      return <ClockIcon />;
    default:
      return undefined;
  }
}

/** What a keyboard reaches next — the same set Tab would, plus headings held for focus (`tabIndex={-1}`). */
const FOCUSABLE = "a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]";

/**
 * Where a keyboard goes on from `from` once it is gone: the first focusable
 * element after it in document order — which on the start page is the
 * reading order on every width — or, with nothing after it (an empty recent
 * list on the desk), the last one before it. Never the page body.
 */
function focusableAfter(from: HTMLElement | null): HTMLElement | null {
  if (from === null) return null;
  // Not inside a block the page holds back (`hidden`): the start page keeps
  // „Zuletzt bearbeitet" mounted but hidden on a first visit (UX gate round
  // 1, #1), and `focus()` on a link in there does nothing — focus would stay
  // on the button that just left, which is the page body.
  const outside = [...document.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (candidate) => !from.contains(candidate) && candidate.closest("[hidden]") === null,
  );
  return (
    outside.find((candidate) => from.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING) ??
    outside.reverse().find((candidate) => from.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_PRECEDING) ??
    null
  );
}

/**
 * One-off outcomes for the sender, on the start page, by need (UX gate
 * round 2, #4). A declined or lapsed request leaves the Vorgang with me, so
 * the notice comes first and leads on — to hand it over again, or to it
 * („Zum Vorgang", UX ruling 11); a taken-over one has no way left to offer.
 *
 * Closing sets `notice_seen_at` — exactly once, and following „Zum Vorgang"
 * counts as reading it. The notice disappears at once; should the write
 * fail, it comes back on the next visit — nothing is lost. Focus goes
 * to the next notice, and after the last one to whatever follows the block
 * in the DOM, which is the reading order (on the desk the requests to me or
 * the recent list, on the phone the requests or the steel areas) — never to
 * the page body.
 *
 * Only the first notice stands open; the rest wait behind „N weitere
 * Meldungen" (screen check S-7, UX gate round 1): three notices at 390 px
 * pushed a request's „Annehmen" to y 992 — a notice wants to be read, a
 * request wants an answer before it lapses.
 */
export function HandoverNotices() {
  const notices = useHandoverNotices();
  const client = useQueryClient();
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const [announcement, setAnnouncement] = useState("");
  const sectionRef = useRef<HTMLElement>(null);
  const closeButtons = useRef(new Map<string, HTMLButtonElement>());
  const seen = useRef(new Set<string>());
  const focusWish = useRef<{ notice: string } | { element: HTMLElement | null } | null>(null);
  // At 600 px the × stays top right and „Zum Vorgang" drops under the
  // sentence (StateMessage.module.css) — so there the × comes first in the
  // DOM too, or Tab would climb back up from the link to it (Task 13 fix
  // round 1).
  const narrow = useNarrow("(width <= 600px)");
  // Bumped when a reload came back: the render it causes carries the focus wish.
  const [, setReloaded] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const listId = useId();

  const list = Array.isArray(notices.data) ? notices.data : undefined;
  const items = byNeed((list ?? []).filter((notice) => !closed.has(notice.handover.id)));
  // Closing the one in view brings the next one up — `close` hands focus to
  // it by the same order, so it is always drawn by then.
  const shown = expanded ? items : items.slice(0, 1);
  const more = items.length - 1;

  useArrivals(list, noticeId, (arrived) => setAnnouncement(arrived.map(noticeSentence).join(" ")));

  useEffect(() => {
    const wish = focusWish.current;
    if (wish === null) return;
    focusWish.current = null;
    ("notice" in wish ? closeButtons.current.get(wish.notice) : wish.element)?.focus();
  });

  function markSeen(id: string) {
    if (seen.current.has(id)) return;
    seen.current.add(id);
    setClosed((previous) => new Set(previous).add(id));
    markNoticeSeen(id)
      .catch(() => {
        // Not written: the server still holds it unseen, so it comes back
        // on the next visit — see above. Closing it then writes again.
        seen.current.delete(id);
      })
      .finally(() => {
        void client.invalidateQueries({ queryKey: NOTICES_KEY });
      });
  }

  function close(id: string) {
    const order = items.map(noticeId);
    const at = order.indexOf(id);
    const neighbour = order[at + 1] ?? order[at - 1];
    // Asked before the block may vanish: after the last notice, what follows it.
    focusWish.current = neighbour !== undefined ? { notice: neighbour } : { element: focusableAfter(sectionRef.current) };
    markSeen(id);
  }

  /**
   * „Erneut laden" leaves with the failure it answers, and focus would fall
   * to the page with it (Task 13 review, I3). A reload that came back puts
   * focus on the first notice's „Schließen" — or, with none to show, on what
   * follows the block, found now while the block still stands. A reload that
   * failed again keeps its button, and the focus on it.
   */
  function reload() {
    const after = focusableAfter(sectionRef.current);
    void notices.refetch().then((result) => {
      if (result.isError) return;
      const first = byNeed(Array.isArray(result.data) ? result.data : []).find(
        (notice) => !closed.has(notice.handover.id),
      );
      focusWish.current = first !== undefined ? { notice: first.handover.id } : { element: after };
      setReloaded((count) => count + 1);
    });
  }

  // Also while fetching again after a failure — see `IncomingHandovers`.
  const failed = list === undefined && (notices.isError === true || (notices.errorUpdateCount ?? 0) > 0);
  if (items.length === 0 && !failed) return null;

  return (
    <section ref={sectionRef} className={styles.notices} aria-label="Meldungen zu deinen Übergaben">
      <p role="status" className="visually-hidden">
        {announcement}
      </p>
      {failed ? (
        // The record's and the handover's one notice pattern (UX gate round
        // 1, #3). A fetch that failed is not a refusal, so it stays neutral
        // like the register's and the record's load failures (--st-conflict
        // is for refusals only). No live region, as before: a region mounted
        // together with its text is not read out (StateMessage's `live`).
        <StateMessage
          tone="neutral"
          live={false}
          title="Meldungen zu deinen Übergaben sind gerade nicht abrufbar — versuch es gleich noch einmal."
          action={
            <Button
              variant="quiet"
              flush
              aria-disabled={notices.isFetching === true ? true : undefined}
              onClick={reload}
            >
              {notices.isFetching === true ? "Wird geladen …" : "Erneut laden"}
            </Button>
          }
        />
      ) : (
        <ul id={listId} className={styles.noticeList}>
          {shown.map((notice) => {
            const id = notice.handover.id;
            const sentence = noticeSentence(notice);
            const stillMine = noticeAsks(notice);
            const decidedAt = notice.handover.decidedAt;
            const caseId = notice.handover.caseId;
            return (
              <li key={id}>
                {/* One notice pattern with the record and the handover (UX
                    gate round 1, #3): the sentence, the Vorgang and when in
                    the body, the ways on beside it, the × top right. A
                    request that came back asks for something (round 2, #4):
                    ink at its edge, and first „Neu übergeben …" — the
                    record opens the handover dialog for it (`?uebergeben=1`)
                    — then „Zum Vorgang". Taken over asks for nothing. */}
                <StateMessage
                  tone="neutral"
                  strong={stillMine}
                  icon={noticeIcon(notice)}
                  live={false}
                  title={sentence}
                  action={
                    stillMine ? (
                      <span className={styles.noticeActions}>
                        <Link
                          className={styles.noticeAgain}
                          to={`/vorgang/${caseId}?uebergeben=1`}
                          aria-label={`Neu übergeben … „${notice.caseTitle}“`}
                          onClick={() => markSeen(id)}
                        >
                          Neu übergeben …
                        </Link>
                        <Link
                          className={`inline-link ${styles.noticeLink}`}
                          to={`/vorgang/${caseId}`}
                          onClick={() => markSeen(id)}
                        >
                          Zum Vorgang
                        </Link>
                      </span>
                    ) : undefined
                  }
                  onDismiss={() => close(id)}
                  dismissLabel={`Schließen: ${sentence}`}
                  dismissRef={(node) => {
                    if (node !== null) closeButtons.current.set(id, node);
                    else closeButtons.current.delete(id);
                  }}
                  dismissFirst={narrow}
                >
                  <span className="num">{notice.caseNo}</span>
                  {decidedAt !== null ? (
                    <>
                      {" · "}
                      <time dateTime={decidedAt}>
                        <Figures text={activityTime(decidedAt)} />
                      </time>
                    </>
                  ) : null}
                </StateMessage>
              </li>
            );
          })}
        </ul>
      )}
      {!failed && more > 0 ? (
        <Button
          variant="quiet"
          flush
          className={styles.moreNotices}
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded((open) => !open)}
        >
          {expanded ? (
            "Weniger zeigen"
          ) : (
            <span>
              <span className="tabular">{more}</span> {more === 1 ? "weitere Meldung" : "weitere Meldungen"}
            </span>
          )}
        </Button>
      ) : null}
    </section>
  );
}
